// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module DeviceBreakdown
 * @package @geenius/analytics/react-css
 * @description Renders the React CSS device-breakdown visualization that
 * compares desktop, tablet, and mobile traffic. The component pairs a
 * proportional bar with a simple legend for quick distribution scans.
 */

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Progress,
  Skeleton,
} from "@geenius/ui/react-css";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

/**
 * Props for rendering the React CSS device-breakdown widget.
 *
 * @property breakdown Device counts grouped by desktop, tablet, and mobile.
 * @property isLoading Whether the widget should render placeholder rows.
 * @property className Optional class name merged into the wrapper.
 */
export interface DeviceBreakdownProps {
  breakdown: { desktop: number; tablet: number; mobile: number };
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  className?: string;
}

/**
 * Renders the React CSS device-breakdown widget.
 *
 * @param props Device counts and optional wrapper className.
 * @returns A device distribution widget for analytics dashboards.
 */
export function DeviceBreakdown({
  breakdown,
  className,
  error,
  isLoading,
  onRetry,
}: DeviceBreakdownProps): React.ReactElement {
  const safeBreakdown = {
    desktop: Number.isFinite(breakdown.desktop)
      ? Math.max(0, breakdown.desktop)
      : 0,
    tablet: Number.isFinite(breakdown.tablet)
      ? Math.max(0, breakdown.tablet)
      : 0,
    mobile: Number.isFinite(breakdown.mobile)
      ? Math.max(0, breakdown.mobile)
      : 0,
  };
  const total =
    safeBreakdown.desktop + safeBreakdown.tablet + safeBreakdown.mobile || 1;
  const pct = (value: number): string =>
    `${((value / total) * 100).toFixed(1)}%`;
  const copy = useAnalyticsCopy();

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle data-analytics-section-title>
            {copy.t("analytics.devices.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="gn-analytics-device-breakdown">
          <ErrorState error={error} onRetry={onRetry} />
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle data-analytics-section-title>
            {copy.t("analytics.devices.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="gn-analytics-device-breakdown gn-analytics-device-breakdown--loading"
            data-analytics-device-breakdown-loading
            aria-busy="true"
          >
            {Array.from(
              { length: 3 },
              (_, index) => `device-breakdown-skeleton-${index}`,
            ).map((skeletonId) => (
              <div
                key={skeletonId}
                className="gn-analytics-device-breakdown__loading-row"
              >
                <Skeleton height={20} />
                <Skeleton height={10} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const segments = [
    {
      device: "desktop",
      value: safeBreakdown.desktop,
      icon: "💻",
      variant: "secondary" as const,
    },
    {
      device: "tablet",
      value: safeBreakdown.tablet,
      icon: "📟",
      variant: "warning" as const,
    },
    {
      device: "mobile",
      value: safeBreakdown.mobile,
      icon: "📱",
      variant: "success" as const,
    },
  ];

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle data-analytics-section-title>
          {copy.t("analytics.devices.title")}
        </CardTitle>
      </CardHeader>
      <CardContent
        className="gn-analytics-device-breakdown"
        data-analytics-device-breakdown
      >
        {segments.map((segment) => (
          <div
            key={segment.device}
            data-analytics-device-item
            data-device={segment.device}
          >
            <div className="gn-analytics-device-breakdown__item-header">
              <Badge variant={segment.variant}>
                {segment.icon} {copy.deviceLabel(segment.device)}
              </Badge>
              <span data-analytics-device-pct>{pct(segment.value)}</span>
            </div>
            <Progress value={segment.value} max={total} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
